import { safeParseJson } from './apiBaseUrl';

/**
 * User Activity Tracker
 * Automatically tracks user interactions and sends them to the backend
 */

class ActivityTracker {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.isTracking = false;
    this.lastActivityTime = Date.now();
    this.activityQueue = [];
    this.batchSize = 5;
    this.batchTimeout = 2000; // 2 seconds
    this.heartbeatInterval = 30000; // 30 seconds
    this.heartbeatTimer = null;
    this.isAuthenticated = this.checkAuthentication();
    this.apiBaseUrl = this.resolveApiBaseUrl();
    
    // Throttle settings
    this.throttleDelay = 1000; // 1 second between activity updates
    this.lastThrottledActivity = 0;
    
    this.init();
  }

  resolveApiBaseUrl() {
    const configuredUrl = (process.env.REACT_APP_API_URL || '').trim();
    const fallbackUrl = typeof window !== 'undefined' && window.location ? window.location.origin : '';
    const baseUrl = (configuredUrl || fallbackUrl || '').trim();
    return baseUrl ? baseUrl.replace(/\/+$/, '') : '';
  }

  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  checkAuthentication() {
    // Check if user is authenticated by looking for auth token
    const token = localStorage.getItem('authToken') || 
                  document.cookie.split(';').find(c => c.trim().startsWith('authToken='));
    return !!token;
  }

  init() {
    // Always initialize tracking, but with different behavior for authenticated vs anonymous users
    this.startTracking();
    this.setupEventListeners();
    
    if (this.isAuthenticated) {
      this.startHeartbeat();
      console.log('ActivityTracker: Initialized for authenticated session', this.sessionId);
    } else {
      console.log('ActivityTracker: Initialized for anonymous session', this.sessionId);
    }
  }

  startTracking() {
    this.isTracking = true;
  }

  stopTracking() {
    this.isTracking = false;
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  setupEventListeners() {
    // Track clicks
    document.addEventListener('click', (event) => {
      this.trackClick(event);
    }, { passive: true });

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      this.trackVisibilityChange();
    });

    // Track beforeunload (user leaving page)
    window.addEventListener('beforeunload', () => {
      this.trackPageLeave();
    });

    // Track keyboard activity
    document.addEventListener('keydown', (event) => {
      this.trackKeyboardActivity(event);
    }, { passive: true });

    // Track mouse movement (throttled)
    let mouseMoveTimeout;
    document.addEventListener('mousemove', () => {
      clearTimeout(mouseMoveTimeout);
      mouseMoveTimeout = setTimeout(() => {
        this.trackMouseActivity();
      }, 5000); // Track mouse activity every 5 seconds
    }, { passive: true });

    // Track scroll activity (throttled)
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.trackScrollActivity();
      }, 2000); // Track scroll activity every 2 seconds
    }, { passive: true });

    // Track route changes (for SPA navigation)
    this.setupRouteChangeTracking();
  }

  trackClick(event) {
    if (!this.isTracking) return;

    const clickData = {
      element: event.target.tagName,
      elementId: event.target.id || null,
      elementClass: event.target.className || null,
      elementText: event.target.textContent?.substring(0, 100) || null,
      x: event.clientX,
      y: event.clientY,
      timestamp: new Date().toISOString()
    };

    this.queueActivity('click', clickData);
  }

  trackKeyboardActivity(event) {
    if (!this.isTracking) return;

    // Only track certain key events to avoid spam
    const importantKeys = ['Enter', 'Space', 'Tab', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (importantKeys.includes(event.key)) {
      const keyboardData = {
        key: event.key,
        code: event.code,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        timestamp: new Date().toISOString()
      };

      this.queueActivity('keyboard', keyboardData);
    }
  }

  trackMouseActivity() {
    if (!this.isTracking) return;

    const mouseData = {
      timestamp: new Date().toISOString(),
      type: 'mouse_movement'
    };

    this.queueActivity('mouse', mouseData);
  }

  trackScrollActivity() {
    if (!this.isTracking) return;

    const scrollData = {
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      scrollHeight: document.documentElement.scrollHeight,
      clientHeight: document.documentElement.clientHeight,
      timestamp: new Date().toISOString()
    };

    this.queueActivity('scroll', scrollData);
  }

  setupRouteChangeTracking() {
    // Track initial page view
    this.trackPageView();

    // Override pushState and replaceState to track route changes
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = (...args) => {
      originalPushState.apply(window.history, args);
      setTimeout(() => this.trackPageView(), 100); // Small delay to ensure DOM is updated
    };

    window.history.replaceState = (...args) => {
      originalReplaceState.apply(window.history, args);
      setTimeout(() => this.trackPageView(), 100);
    };

    // Track popstate events (back/forward navigation)
    window.addEventListener('popstate', () => {
      setTimeout(() => this.trackPageView(), 100);
    });
  }

  trackVisibilityChange() {
    if (!this.isTracking) return;

    const visibilityData = {
      hidden: document.hidden,
      visibilityState: document.visibilityState,
      timestamp: new Date().toISOString()
    };

    this.queueActivity('visibility_change', visibilityData);
  }

  trackPageLeave() {
    if (!this.isTracking) return;

    // Send immediately without queuing
    this.sendActivity('page_leave', {
      timestamp: new Date().toISOString(),
      url: window.location.href
    }, true);
  }

  trackPageView(pageData = {}) {
    if (!this.isTracking) return;

    const data = {
      page: window.location.pathname, // This is what the analytics query looks for
      url: window.location.href,
      title: document.title,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      ...pageData
    };

    this.queueActivity('page_view', data);
  }

  trackChat(chatData) {
    if (!this.isTracking) return;

    this.queueActivity('chat', {
      ...chatData,
      timestamp: new Date().toISOString()
    });
  }

  trackButtonClick(buttonData) {
    if (!this.isTracking) return;

    this.queueActivity('button_click', {
      ...buttonData,
      timestamp: new Date().toISOString()
    });
  }

  trackLogin(loginData = {}) {
    if (!this.isTracking) return;

    this.sendActivity('login', {
      ...loginData,
      timestamp: new Date().toISOString()
    }, true);
  }

  queueActivity(activityType, activityData) {
    if (!this.isTracking) return;

    // Throttle activity updates
    const now = Date.now();
    if (now - this.lastThrottledActivity < this.throttleDelay) {
      return;
    }
    this.lastThrottledActivity = now;

    this.activityQueue.push({
      type: activityType,
      data: activityData,
      timestamp: new Date().toISOString()
    });

    this.lastActivityTime = now;

    // Process queue if it reaches batch size
    if (this.activityQueue.length >= this.batchSize) {
      this.processQueue();
    } else {
      // Set timeout to process queue after delay
      setTimeout(() => {
        this.processQueue();
      }, this.batchTimeout);
    }
  }

  async processQueue() {
    if (this.activityQueue.length === 0) return;

    const activities = [...this.activityQueue];
    this.activityQueue = [];

    try {
      const success = await this.sendActivity('batch', {
        activities: activities,
        count: activities.length
      });
      
      // If failed, only re-queue if we haven't exceeded max retries
      if (!success && this.activityQueue.length < 50) {
        // Only re-queue a limited number of times to prevent infinite loops
        const retryCount = activities[0]?.retryCount || 0;
        if (retryCount < 3) {
          activities.forEach(a => a.retryCount = (a.retryCount || 0) + 1);
          this.activityQueue.unshift(...activities);
        }
      }
    } catch (error) {
      // This shouldn't happen now since sendActivity doesn't throw, but just in case
      if (process.env.NODE_ENV === 'development') {
        console.warn('ActivityTracker: Error processing queue:', error);
      }
      // Don't re-queue on error to prevent infinite retries
    }
  }

  async sendActivity(activityType, activityData, immediate = false) {
    try {
      const headers = {
        'Content-Type': 'application/json'
      };

      // Add authorization header only for authenticated users
      if (this.isAuthenticated) {
        headers['Authorization'] = `Bearer ${this.getAuthToken()}`;
      }

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(`${this.apiBaseUrl}/api/activity/update`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          sessionId: this.sessionId,
          activityType: activityType,
          activityData: activityData,
          isAuthenticated: this.isAuthenticated
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('ActivityTracker: Activity update failed:', response.status, response.statusText);
        }
        return false;
      }

      await safeParseJson(response); // avoid .json() TypeError when server returns non-JSON
      if (immediate && process.env.NODE_ENV === 'development') {
        console.log('ActivityTracker: Activity sent immediately:', activityType);
      }
      return true;
    } catch (error) {
      // Silently handle network/parse errors - avoid console spam
      if (process.env.NODE_ENV === 'development') {
        console.warn('ActivityTracker: Failed to send activity:', error.name || 'network/parse error');
      }
      return false;
    }
  }

  getAuthToken() {
    return localStorage.getItem('authToken') || 
           document.cookie.split(';').find(c => c.trim().startsWith('authToken='))?.split('=')[1];
  }

  startHeartbeat() {
    this.heartbeatTimer = setInterval(() => {
      if (this.isTracking && this.isAuthenticated) {
        this.sendActivity('heartbeat', {
          timestamp: new Date().toISOString(),
          lastActivity: this.lastActivityTime
        });
      }
    }, this.heartbeatInterval);
  }

  // Public methods for manual tracking
  trackCustomActivity(type, data) {
    this.queueActivity(type, data);
  }

  // Get current session info
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      isTracking: this.isTracking,
      isAuthenticated: this.isAuthenticated,
      lastActivityTime: this.lastActivityTime,
      queueLength: this.activityQueue.length
    };
  }

  // Force process queue (useful before page unload)
  flush() {
    this.processQueue();
  }
}

// Create global instance
window.activityTracker = new ActivityTracker();

// Activity tracker is initialized immediately when the script loads

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ActivityTracker;
}

export default ActivityTracker;
